/**
 * The one place a "selected" state is drawn.
 *
 * Every switcher in the app — the toggle-button tab strips, the drilldown's
 * router tabs, the MUI `<Tabs>` wrapper, the nav sidebar — used to hand-roll the
 * same three declarations (tinted fill, colored label, 2px edge) with its own
 * copy of the brand hex. They now all call `selectedStateSx()`, so changing how
 * a selected tab looks is one edit, and a new switcher can't drift.
 *
 * Which level to pass is a question about what the switcher DOES, not how it
 * looks — see `level1` / `level2` / `level3` in ./colors.js and section 3 of
 * ./eastview-color-system.md:
 *
 *   level 1 (default) — the URL / breadcrumb changes  → teal
 *   level 2           — the data changes, not the page → steel
 *   level 3           — only the presentation changes  → neutral segmented
 */
import { level1, level2, level3, slate } from "./colors";

export const ACTIVE_LEVEL = { PLACE: 1, DATA: 2 };

/** Tokens for an active level. Anything unknown falls back to level 1. */
export const activeTokens = (level) => (level === 2 ? level2 : level1);

/**
 * Which border carries the 2px indicator, given how the strip is laid out.
 * Horizontal strips underline; vertical lists grow a bar on the inside edge.
 */
const indicatorEdge = (orientation, underlinePosition) => {
  if (orientation === "vertical") {
    return underlinePosition === "right" ? "borderRight" : "borderLeft";
  }
  return underlinePosition === "top" ? "borderTop" : "borderBottom";
};

/**
 * The selected-state style object for one item in a switcher.
 *
 * Spread it into `&.Mui-selected` (ToggleButton / Tab) or `&.active` (NavLink).
 * `variant` matches the switchers' existing vocabulary:
 *
 *   "background" — tinted fill (the default)
 *   "underline"  — no fill, 2px indicator on the edge
 *   "circle"     — pill chip: tinted fill + 1px tinted border
 */
export const selectedStateSx = ({
  level = ACTIVE_LEVEL.PLACE,
  variant = "background",
  orientation = "horizontal",
  underlinePosition = "bottom",
} = {}) => {
  const t = activeTokens(level);
  const edge = indicatorEdge(orientation, underlinePosition);

  if (variant === "underline") {
    return {
      color: t.text,
      fontWeight: t.weight,
      background: "none",
      borderRadius: "0 !important",
      borderTop: "none",
      borderRight: "none",
      borderBottom: "none",
      borderLeft: "none",
      [edge]: `2px solid ${t.indicator}`,
    };
  }

  if (variant === "circle") {
    return {
      color: t.text,
      fontWeight: `${t.weight} !important`,
      background: t.tint,
      border: `1px solid ${t.border} !important`,
    };
  }

  return {
    color: t.text,
    fontWeight: t.weight,
    background: t.tint,
  };
};

/** Resting (unselected) label color + the hover wash that precedes selection. */
export const restingStateSx = ({ level = ACTIVE_LEVEL.PLACE } = {}) => {
  const t = activeTokens(level);
  return {
    color: slate[700],
    "&:hover": { color: t.text, background: t.hoverTint },
  };
};

/**
 * A selected row / card that is *not* a switcher — a picked counterparty card,
 * a highlighted grid row. Same tint, but the label keeps normal ink so the card
 * doesn't read as a link.
 */
export const selectedSurfaceSx = ({
  level = ACTIVE_LEVEL.PLACE,
  bordered = true,
} = {}) => {
  const t = activeTokens(level);
  return {
    backgroundColor: t.tint,
    ...(bordered && { borderColor: t.border }),
  };
};

/**
 * LEVEL 3 — the neutral white-card-in-gray-track segmented control, for
 * view-mode toggles (Standard ↔ Ledger, table ↔ cards). Pass to a
 * `<ToggleButtonGroup sx={...}>`; it styles its own buttons.
 */
export const segmentedGroupSx = ({ fontSize = 12.5 } = {}) => ({
  backgroundColor: level3.track,
  borderRadius: "9px",
  padding: "3px",
  gap: "2px",
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontWeight: level3.weight,
    fontSize,
    border: "none",
    borderRadius: "6px !important",
    padding: "5px 13px",
    color: level3.restText,
    "&:hover": { backgroundColor: "rgba(15,23,42,.04)" },
    "&.Mui-selected": {
      backgroundColor: level3.activeBg,
      color: level3.activeText,
      boxShadow: level3.activeShadow,
      "&:hover": { backgroundColor: level3.activeBg },
    },
  },
});

export default selectedStateSx;
